import { getDatabase, ref, get, update } from 'firebase/database';

/**
 * Notifications used to live at users/{uid}/notifications. Anything that
 * listened to `users` (the user directory is loaded on every page) pulled
 * every employee's whole notification history with it, so they now live at
 * notifications/{uid}. These helpers move what is still at the old path.
 *
 * Each user moves in one atomic update: the copies are written and the old
 * node is cleared together, so a notification is never lost or duplicated.
 * A notification already at the new path is kept as it is.
 */

const buildUserMove = (updates, uid, oldNotifications, existing = {}) => {
  let moved = 0;
  Object.entries(oldNotifications || {}).forEach(([id, notification]) => {
    if (!existing[id]) {
      updates[`notifications/${uid}/${id}`] = notification;
      moved += 1;
    }
  });
  updates[`users/${uid}/notifications`] = null;
  return moved;
};

/**
 * Move one user's notifications. Runs on sign-in, which also catches anything
 * a browser still running an old build wrote to the old path.
 */
export const migrateUserNotifications = async (uid) => {
  if (!uid) return 0;
  const db = getDatabase();
  const oldSnap = await get(ref(db, `users/${uid}/notifications`));
  if (!oldSnap.exists()) return 0;

  const newSnap = await get(ref(db, `notifications/${uid}`));
  const updates = {};
  const moved = buildUserMove(updates, uid, oldSnap.val(), newSnap.val() || {});
  await update(ref(db), updates);
  return moved;
};

/**
 * Move every user's notifications. Reads the whole `users` node once, which
 * is the download this migration exists to stop, so run it once.
 */
export const migrateAllNotifications = async (onProgress = () => {}) => {
  const db = getDatabase();
  const [usersSnap, newSnap] = await Promise.all([
    get(ref(db, 'users')),
    get(ref(db, 'notifications')),
  ]);
  const users = usersSnap.val() || {};
  const existingByUser = newSnap.val() || {};

  const pending = Object.entries(users).filter(([, u]) => u && u.notifications);
  const result = { users: 0, notifications: 0, failed: [] };

  for (const [uid, u] of pending) {
    const updates = {};
    const moved = buildUserMove(updates, uid, u.notifications, existingByUser[uid] || {});
    try {
      await update(ref(db), updates);
      result.users += 1;
      result.notifications += moved;
    } catch (error) {
      result.failed.push({ uid, error: error.message });
    }
    onProgress({ done: result.users + result.failed.length, total: pending.length });
  }

  return result;
};
